import { useCallback, useEffect, useMemo, useState } from 'react';
import { Event, MatchBox } from '../types';
import { EnrichedMatchRequest, mockBackend } from '../services/mockBackend';
import EventDescription from './EventDescription';
import StatusBadge from './StatusBadge';
import { userFacingError } from '../utils/errors';

type MatchesPanelProps = {
  userId: string;
  refreshToken: number;
  onActionComplete: () => void;
};

const boxOptions: Array<{ label: string; value: MatchBox }> = [
  { label: 'Recebidos', value: 'received' },
  { label: 'Enviados', value: 'sent' },
  { label: 'Em negociação', value: 'negotiation' },
  { label: 'Concluídos', value: 'completed' },
];

const normalizeSingleLineText = (value: string) => value.replace(/[\r\n\t]+/g, ' ');

const isSingleLineText = (value: string) =>
  Array.from(value).every((character) => {
    const code = character.charCodeAt(0);
    return code >= 32 && code !== 127;
  });

const MatchesPanel = ({ userId, refreshToken, onActionComplete }: MatchesPanelProps) => {
  const [box, setBox] = useState<MatchBox>('received');
  const [items, setItems] = useState<EnrichedMatchRequest[]>([]);
  const [myEvents, setMyEvents] = useState<Event[]>([]);
  const [sourceEventId, setSourceEventId] = useState('');
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<
    Awaited<ReturnType<typeof mockBackend.matches.searchMatchCandidates>>
  >([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [note, setNote] = useState('');
  const [selectedMatch, setSelectedMatch] = useState<EnrichedMatchRequest | null>(null);
  const [responseNote, setResponseNote] = useState('');
  const [feedback, setFeedback] = useState('');
  const [proposalForm, setProposalForm] = useState({
    title: '',
    startAt: '',
    endAt: '',
    location: '',
  });

  const loadMatches = useCallback(async () => {
    setFeedback('');
    try {
      const [matches, events] = await Promise.all([
        mockBackend.matches.listCreatorMatches(userId, box),
        mockBackend.events.listCreatorEvents(userId),
      ]);
      setItems(matches);
      setMyEvents(
        events.filter((event) =>
          ['approved', 'pending_review', 'changes_requested'].includes(event.status)
        )
      );
      setSelectedMatch((current) =>
        current ? matches.find((item) => item.id === current.id) || null : current
      );
    } catch (loadError) {
      setFeedback(userFacingError(loadError, 'Não foi possível carregar os matches.'));
    }
  }, [box, userId]);

  useEffect(() => {
    void loadMatches();
  }, [loadMatches, refreshToken]);

  useEffect(() => {
    if (!selectedMatch) {
      setResponseNote('');
      setProposalForm({ title: '', startAt: '', endAt: '', location: '' });
      return;
    }

    setProposalForm({
      title: selectedMatch.proposal.title,
      startAt: selectedMatch.proposal.startAt.slice(0, 16),
      endAt: selectedMatch.proposal.endAt.slice(0, 16),
      location: selectedMatch.proposal.location,
    });
  }, [selectedMatch]);

  const searchCandidates = async () => {
    setFeedback('');
    try {
      const results = await mockBackend.matches.searchMatchCandidates({
        query,
        eventId: sourceEventId || undefined,
      });
      setCandidates(results);
    } catch (searchError) {
      setFeedback(userFacingError(searchError, 'Não foi possível buscar eventos compatíveis.'));
    }
  };

  const selectedCandidate = useMemo(
    () => candidates.find((candidate) => candidate.id === selectedCandidateId) || null,
    [candidates, selectedCandidateId]
  );

  const proposalTitleInvalid = !isSingleLineText(proposalForm.title);
  const proposalLocationInvalid = !isSingleLineText(proposalForm.location);

  const validateProposalForm = () => {
    if (proposalTitleInvalid) return 'Título proposto contém caracteres inválidos.';
    if (proposalLocationInvalid) return 'Local consolidado contém caracteres inválidos.';
    return '';
  };

  const createManualMatch = async () => {
    setFeedback('');
    if (!sourceEventId || !selectedCandidateId || !note.trim()) {
      return;
    }

    const proposalError = validateProposalForm();
    if (proposalError) {
      setFeedback(proposalError);
      return;
    }

    try {
      await mockBackend.matches.createMatchRequest({
        sourceEventId,
        targetEventId: selectedCandidateId,
        initiatorUserId: userId,
        note,
        checkedCompatibility: true,
        proposal: {
          title: proposalForm.title || undefined,
          startAt: proposalForm.startAt ? new Date(proposalForm.startAt).toISOString() : undefined,
          endAt: proposalForm.endAt ? new Date(proposalForm.endAt).toISOString() : undefined,
          location: proposalForm.location || undefined,
        },
      });

      setSourceEventId('');
      setQuery('');
      setCandidates([]);
      setSelectedCandidateId('');
      setNote('');
      setProposalForm({ title: '', startAt: '', endAt: '', location: '' });
      await loadMatches();
      onActionComplete();
    } catch (createError) {
      setFeedback(userFacingError(createError, 'Não foi possível enviar o pedido de match.'));
    }
  };

  const canRespond =
    selectedMatch &&
    selectedMatch.awaitingUser?.id === userId &&
    ['sent', 'viewed', 'counter_proposed'].includes(selectedMatch.status);

  return (
    <div className="d-grid gap-4">
      <div className="card bg-onyx border-0 shadow-lg">
        <div className="card-body p-4">
          <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
            <div>
              <h5 className="text-white mb-1">Abrir match manual</h5>
              <p className="text-white-50 mb-0">
                Escolha um evento aprovado ou em revisão. Se ainda não estiver aprovado, o match
                fica registrado e só é enviado após a curadoria.
              </p>
            </div>
            <span className="badge bg-brand text-white">Busca manual</span>
          </div>

          {feedback && <div className="alert alert-danger py-2">{feedback}</div>}

          <div className="row g-3">
            <div className="col-md-5">
              <label className="form-label text-white">Seu evento</label>
              <select
                className="form-select input-contrast"
                value={sourceEventId}
                onChange={(event) => setSourceEventId(event.target.value)}
              >
                <option value="">Selecione</option>
                {myEvents.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title} {item.status !== 'approved' ? '(aguarda aprovação)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label text-white">Pesquisar alvo</label>
              <input
                className="form-control input-contrast"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Título, instituição, local..."
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button type="button" className="btn btn-outline-light w-100" onClick={searchCandidates}>
                Buscar
              </button>
            </div>

            {candidates.length > 0 && (
              <div className="col-12">
                <div className="match-results-grid">
                  {candidates.map((candidate) => (
                    <button
                      type="button"
                      key={candidate.id}
                      className={`match-result-card ${
                        selectedCandidateId === candidate.id ? 'selected' : ''
                      }`}
                      onClick={() => setSelectedCandidateId(candidate.id)}
                    >
                      <strong>{candidate.title}</strong>
                      <span>{candidate.institution}</span>
                      <small>{candidate.location}</small>
                      <small>{new Date(candidate.startAt).toLocaleString('pt-BR')}</small>
                      <small>
                        {candidate.targetAudience} · {candidate.estimatedAudience || 'sem estimativa'}
                      </small>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedCandidate && (
              <>
                <div className="col-12">
                  <div className="selected-match-box">
                    <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                      <div>
                        <strong className="text-white d-block">{selectedCandidate.title}</strong>
                        <span className="text-white-50 small">{selectedCandidate.institution}</span>
                      </div>
                      <span className="badge bg-dark text-white border border-secondary">
                        {selectedCandidate.accessType === 'ingressos' ? 'Ingressos' : 'Aberto'}
                      </span>
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <div className="metric-card">
                          <span>Data</span>
                          <strong>{new Date(selectedCandidate.startAt).toLocaleString('pt-BR')}</strong>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="metric-card">
                          <span>Local</span>
                          <strong>{selectedCandidate.location}</strong>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="metric-card">
                          <span>Público</span>
                          <strong>
                            {selectedCandidate.targetAudience} · {selectedCandidate.estimatedAudience || 'sem estimativa'}
                          </strong>
                        </div>
                      </div>
                    </div>
                    <EventDescription
                      description={selectedCandidate.description}
                      format={selectedCandidate.descriptionFormat}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label text-white">Título proposto</label>
                  <input
                    className={`form-control input-contrast${proposalTitleInvalid ? ' is-invalid' : ''}`}
                    value={proposalForm.title}
                    onChange={(event) =>
                      setProposalForm((current) => ({
                        ...current,
                        title: normalizeSingleLineText(event.target.value),
                      }))
                    }
                    placeholder="Deixe vazio para usar o padrão"
                  />
                  {proposalTitleInvalid && (
                    <div className="invalid-feedback">Título proposto contém caracteres inválidos.</div>
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label text-white">Início proposto</label>
                  <input
                    className="form-control input-contrast"
                    type="datetime-local"
                    value={proposalForm.startAt}
                    onChange={(event) =>
                      setProposalForm((current) => ({ ...current, startAt: event.target.value }))
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label text-white">Término proposto</label>
                  <input
                    className="form-control input-contrast"
                    type="datetime-local"
                    value={proposalForm.endAt}
                    onChange={(event) =>
                      setProposalForm((current) => ({ ...current, endAt: event.target.value }))
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label text-white">Local consolidado</label>
                  <input
                    className={`form-control input-contrast${proposalLocationInvalid ? ' is-invalid' : ''}`}
                    value={proposalForm.location}
                    onChange={(event) =>
                      setProposalForm((current) => ({
                        ...current,
                        location: normalizeSingleLineText(event.target.value),
                      }))
                    }
                    placeholder="Local ou formato da agenda consolidada"
                  />
                  {proposalLocationInvalid && (
                    <div className="invalid-feedback">Local consolidado contém caracteres inválidos.</div>
                  )}
                </div>
              </>
            )}

            <div className="col-12">
              <label className="form-label text-white">Justificativa</label>
              <textarea
                className="form-control input-contrast"
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Explique a compatibilidade e o ganho do merge."
              />
            </div>
            <div className="col-12">
              <button
                type="button"
                className="btn btn-brand"
                disabled={!sourceEventId || !selectedCandidateId || !note.trim()}
                onClick={createManualMatch}
              >
                Enviar pedido de match
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card bg-onyx border-0 shadow-lg">
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
            <div>
              <h5 className="text-white mb-1">Central de match</h5>
              <p className="text-white-50 mb-0">
                Recebidos, enviados, negociações em aberto e históricos convertidos.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {boxOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`btn btn-sm ${box === option.value ? 'btn-brand' : 'btn-outline-light'}`}
                  onClick={() => setBox(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {feedback && <div className="alert alert-danger py-2">{feedback}</div>}

          {items.length === 0 ? (
            <div className="empty-box">Nenhum match nesta caixa.</div>
          ) : (
            <div className="row g-3">
              {items.map((item) => (
                <div className="col-xl-6" key={item.id}>
                  <button
                    type="button"
                    className="match-summary-card"
                    onClick={() => setSelectedMatch(item)}
                  >
                    <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                      <div>
                        <strong className="d-block text-white">{item.sourceEvent.title}</strong>
                        <span className="text-white-50 small">↔ {item.targetEvent.title}</span>
                      </div>
                      <StatusBadge kind="match" status={item.status} />
                    </div>
                    <div className="text-white-50 small">
                      {item.status === 'pending_event_approval'
                        ? 'Será enviado quando o evento origem for aprovado'
                        : `Aguardando: ${item.awaitingUser?.name || 'Administração'}`}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedMatch && (
        <div className="event-modal-backdrop" onClick={() => setSelectedMatch(null)}>
          <div className="event-modal submission-modal card bg-onyx border-0 shadow-lg" onClick={(event) => event.stopPropagation()}>
            <div className="card-body p-4">
              <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <StatusBadge kind="match" status={selectedMatch.status} />
                    <span className="badge bg-dark text-white border border-secondary">
                      {selectedMatch.status === 'pending_event_approval'
                        ? 'Aguardando aprovação do evento origem'
                        : `Aguardando ${selectedMatch.awaitingUser?.name || 'Administração'}`}
                    </span>
                  </div>
                  <h4 className="text-white mb-1">Match entre eventos</h4>
                  <p className="text-white-50 mb-0">
                    {selectedMatch.sourceEvent.title} ↔ {selectedMatch.targetEvent.title}
                  </p>
                </div>
                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => setSelectedMatch(null)}>
                  Fechar
                </button>
              </div>

              {selectedMatch.status === 'pending_event_approval' && (
                <div className="selected-match-box mb-4">
                  <strong className="text-white d-block mb-1">Pedido registrado</strong>
                  <div className="text-white-50 small">
                    A outra parte só recebe a solicitação depois que o evento origem for aprovado pela curadoria.
                  </div>
                </div>
              )}

              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="metric-card">
                    <span>Evento origem</span>
                    <strong>{selectedMatch.sourceEvent.title}</strong>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="metric-card">
                    <span>Evento alvo</span>
                    <strong>{selectedMatch.targetEvent.title}</strong>
                  </div>
                </div>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="text-white fw-semibold mb-3">Proposta atual de consolidação</div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-white">Título</label>
                      <input
                        className={`form-control input-contrast${proposalTitleInvalid ? ' is-invalid' : ''}`}
                        value={proposalForm.title}
                        onChange={(event) =>
                          setProposalForm((current) => ({
                            ...current,
                            title: normalizeSingleLineText(event.target.value),
                          }))
                        }
                        placeholder="Título da agenda consolidada"
                        disabled={!canRespond}
                      />
                      {proposalTitleInvalid && (
                        <div className="invalid-feedback">Título contém caracteres inválidos.</div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-white">Início</label>
                      <input
                        className="form-control input-contrast"
                        type="datetime-local"
                        value={proposalForm.startAt}
                        onChange={(event) =>
                          setProposalForm((current) => ({ ...current, startAt: event.target.value }))
                        }
                        disabled={!canRespond}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-white">Fim</label>
                      <input
                        className="form-control input-contrast"
                        type="datetime-local"
                        value={proposalForm.endAt}
                        onChange={(event) =>
                          setProposalForm((current) => ({ ...current, endAt: event.target.value }))
                        }
                        disabled={!canRespond}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-white">Local</label>
                      <input
                        className={`form-control input-contrast${proposalLocationInvalid ? ' is-invalid' : ''}`}
                        value={proposalForm.location}
                        onChange={(event) =>
                          setProposalForm((current) => ({
                            ...current,
                            location: normalizeSingleLineText(event.target.value),
                          }))
                        }
                        placeholder="Local ou formato da agenda consolidada"
                        disabled={!canRespond}
                      />
                      {proposalLocationInvalid && (
                        <div className="invalid-feedback">Local contém caracteres inválidos.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card bg-dark border-0 mb-4">
                <div className="card-body">
                  <div className="text-white fw-semibold mb-3">Conversa</div>
                  <div className="timeline-list">
                    {selectedMatch.conversation.map((message) => (
                      <div key={message.id} className="timeline-item">
                        <strong className="text-white d-block">
                          {message.authorUserId === userId ? 'Você' : 'Outra parte'}
                        </strong>
                        <span className="text-white-50 d-block">{message.body}</span>
                        <small className="text-white-50">
                          {new Date(message.createdAt).toLocaleString('pt-BR')}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {selectedMatch.convertedEvent && (
                <div className="selected-match-box mb-4">
                  <strong className="text-white d-block mb-1">Evento consolidado gerado</strong>
                  <div className="text-white">{selectedMatch.convertedEvent.title}</div>
                  <div className="text-white-50 small">
                    Aguardando revisão administrativa final.
                  </div>
                </div>
              )}

              {canRespond && (
                <div className="card bg-dark border-0 mb-4">
                  <div className="card-body">
                    <label className="form-label text-white">Sua resposta</label>
                    <textarea
                      className="form-control input-contrast"
                      rows={3}
                      value={responseNote}
                      onChange={(event) => setResponseNote(event.target.value)}
                      placeholder="Registre aceite, contraproposta ou devolutiva."
                    />
                  </div>
                </div>
              )}

              {canRespond && (
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={async () => {
                      setFeedback('');
                      try {
                        await mockBackend.matches.acceptMatchRequest(selectedMatch.id, userId, responseNote);
                        await loadMatches();
                        onActionComplete();
                      } catch (acceptError) {
                        setFeedback(userFacingError(acceptError, 'Não foi possível aceitar este match.'));
                      }
                    }}
                  >
                    Aceitar e consolidar
                  </button>
                  <button
                    type="button"
                    className="btn btn-brand"
                    onClick={async () => {
                      setFeedback('');
                      const proposalError = validateProposalForm();
                      if (proposalError) {
                        setFeedback(proposalError);
                        return;
                      }
                      try {
                        await mockBackend.matches.counterMatchRequest(selectedMatch.id, userId, {
                          note: responseNote || 'Enviei uma contraproposta para a consolidação.',
                          proposal: {
                            title: proposalForm.title,
                            startAt: proposalForm.startAt
                              ? new Date(proposalForm.startAt).toISOString()
                              : undefined,
                            endAt: proposalForm.endAt
                              ? new Date(proposalForm.endAt).toISOString()
                              : undefined,
                            location: proposalForm.location,
                          },
                        });
                        await loadMatches();
                        onActionComplete();
                      } catch (counterError) {
                        setFeedback(userFacingError(counterError, 'Não foi possível enviar a contraproposta.'));
                      }
                    }}
                  >
                    Enviar contraproposta
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-light"
                    onClick={async () => {
                      setFeedback('');
                      try {
                        await mockBackend.matches.declineMatchRequest(
                          selectedMatch.id,
                          userId,
                          responseNote || 'Recusei o match por desalinhamento operacional.'
                        );
                        await loadMatches();
                        onActionComplete();
                      } catch (declineError) {
                        setFeedback(userFacingError(declineError, 'Não foi possível recusar este match.'));
                      }
                    }}
                  >
                    Recusar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchesPanel;
